import React, { useState, useEffect, useCallback } from 'react';
import { listUsers, createUser, updateUserAccess, deleteUser } from '../services/apiService';
import '../styles/UserManagementPanel.css';

const ROLE_LABELS = { ADMIN: 'Admin', USER: 'User', VIEWER: 'Viewer' };

const UserManagementPanel = ({ currentUser, onClose }) => {
  const [users, setUsers]         = useState([]);
  const [loadErr, setLoadErr]     = useState('');
  const [formErr, setFormErr]     = useState('');
  const [formOk, setFormOk]       = useState('');
  const [creating, setCreating]   = useState(false);

  // New user form
  const [newUsername, setNewUsername]         = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [newRole, setNewRole]                 = useState('USER');
  const [newAccess, setNewAccess]             = useState('INDEX');
  const [newTelegramChatId, setNewTelegramChatId] = useState('');

  const load = useCallback(async () => {
    setLoadErr('');
    try {
      const data = await listUsers(currentUser.username);
      setUsers(data);
    } catch (e) {
      setLoadErr(e?.response?.data?.error || 'Failed to load users.');
    }
  }, [currentUser.username]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormErr(''); setFormOk('');
    if (!newUsername.trim() || !newPassword) {
      setFormErr('Username and password are required.'); return;
    }
    if (newPassword.length < 6) {
      setFormErr('Password must be at least 6 characters.'); return;
    }
    setCreating(true);
    try {
      await createUser(currentUser.username, newUsername.trim(), newPassword, newRole, newAccess,
        newRole === 'VIEWER' && newTelegramChatId.trim() ? newTelegramChatId.trim() : null);
      setFormOk(`User "${newUsername.trim()}" created.`);
      setNewUsername(''); setNewPassword(''); setNewRole('USER'); setNewAccess('INDEX');
      setNewTelegramChatId('');
      load();
    } catch (e) {
      setFormErr(e?.response?.data?.error || 'Failed to create user.');
    } finally {
      setCreating(false);
    }
  };

  const handleAccessChange = async (username, access) => {
    try {
      await updateUserAccess(currentUser.username, username, access);
      load();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to update access.');
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      await deleteUser(currentUser.username, username);
      load();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to delete user.');
    }
  };

  return (
    <div className="ump-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ump-modal">
        <div className="ump-header">
          <span>User Management</span>
          <button className="ump-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Existing users ───────────────────────────────────────────────── */}
        <div className="ump-section-title">Users</div>
        {loadErr && <div className="ump-error">{loadErr}</div>}
        <table className="ump-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Access</th>
              <th>Telegram</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.username} className={u.username === currentUser.username ? 'ump-self' : ''}>
                <td>
                  {u.username}
                  {u.username === currentUser.username && <span className="ump-you-tag">you</span>}
                </td>
                <td>
                  <span className={`ump-role-badge ump-role-${u.role?.toLowerCase()}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td>
                  {u.role === 'ADMIN' ? (
                    <span className="ump-access-badge ump-access-both">Full Access</span>
                  ) : u.role === 'VIEWER' ? (
                    <span className="ump-access-badge" style={{ background: 'rgba(123,31,162,0.15)', color: '#ce93d8' }}>
                      Trading Setup + OI
                    </span>
                  ) : (
                    <select
                      className="ump-access-select"
                      value={u.access}
                      onChange={e => handleAccessChange(u.username, e.target.value)}
                    >
                      <option value="INDEX">Index only</option>
                      <option value="STOCK">Stocks only</option>
                      <option value="BOTH">Index + Stocks</option>
                    </select>
                  )}
                </td>
                <td style={{ color: '#94a3b8', fontSize: '12px' }}>
                  {u.role === 'VIEWER' ? (u.telegramChatId || '—') : ''}
                </td>
                <td>
                  {u.username !== currentUser.username && (
                    <button
                      className="ump-delete-btn"
                      onClick={() => handleDelete(u.username)}
                      title="Delete user"
                    >
                      🗑
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && !loadErr && (
              <tr><td colSpan={5} className="ump-empty">No users found.</td></tr>
            )}
          </tbody>
        </table>

        {/* ── Create new user ──────────────────────────────────────────────── */}
        <div className="ump-section-title" style={{ marginTop: '24px' }}>Create New User</div>
        <form onSubmit={handleCreate} className="ump-create-form">
          {formErr && <div className="ump-error">{formErr}</div>}
          {formOk  && <div className="ump-success">{formOk}</div>}

          <div className="ump-form-row">
            <div className="ump-form-field">
              <label>Username</label>
              <input
                type="text"
                value={newUsername}
                onChange={e => setNewUsername(e.target.value)}
                placeholder="username"
                disabled={creating}
              />
            </div>
            <div className="ump-form-field">
              <label>Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="min 6 chars"
                disabled={creating}
              />
            </div>
          </div>

          <div className="ump-form-row">
            <div className="ump-form-field">
              <label>Role</label>
              <select value={newRole} onChange={e => setNewRole(e.target.value)} disabled={creating}>
                <option value="USER">User</option>
                <option value="ADMIN">Admin</option>
                <option value="VIEWER">Viewer</option>
              </select>
            </div>
            <div className="ump-form-field">
              <label>Access</label>
              <select value={newAccess} onChange={e => setNewAccess(e.target.value)} disabled={creating}>
                <option value="INDEX">Index only</option>
                <option value="STOCK">Stocks only</option>
                <option value="BOTH">Index + Stocks</option>
              </select>
            </div>
          </div>

          {newRole === 'VIEWER' && (
            <div className="ump-form-row">
              <div className="ump-form-field">
                <label>Telegram Chat ID (optional)</label>
                <input
                  type="text"
                  value={newTelegramChatId}
                  onChange={e => setNewTelegramChatId(e.target.value)}
                  placeholder="@username or numeric chat ID"
                  disabled={creating}
                />
              </div>
            </div>
          )}

          <button type="submit" className="ump-create-btn" disabled={creating}>
            {creating ? 'Creating…' : '+ Create User'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserManagementPanel;
